import { createShortUid, logError } from '@amazon/vinyl-util'
import { ctx } from '../env/playbackContext'
import { getPlaybackEnv } from '../env/playbackEnvRef'
import { previewMetricId } from '../metrics/selectionSourceFromMetricId'
import type { TrackFields } from '../gql/trackFieldsFrag'
import { sanitizeId } from '../util/entityId'
import { getLargestImage } from '../util/imageUtil'
import { MrnIdentifierType, parseMrn } from '../util/mrn'
import type { PlaylistEntry } from './PlaylistController'
import { isPreviewOnlySelections } from './previewOnlySelections'

const { target } = ctx('trackToPlaylistEntry')

export function trackToPlaylistEntry(track: TrackFields): PlaylistEntry | null {
    // Signed-in responses carry the entity MRN (catalog `…:asin:B0…`, or a
    // library `…:objectid:…` for owned/uploaded content); anonymous responses
    // omit `uri` (it's `@include(if: $isAuthenticated)`) but still expose the
    // bare asin via playParams.id — and sample streaming only needs the asin.
    // Prefer the MRN when present, and fall back to playParams.id so anon
    // tracks resolve too. playParams.id is sanitized: some feeds echo a full
    // MRN into it, which would otherwise produce a junk `sample://mrn:…` uri.
    const mrn = track.uri ? parseMrn(track.uri) : null
    const identifier = mrn?.identifier ?? sanitizeId(track.playParams?.id)
    if (!identifier) return null
    const artistName = track.contributingArtists?.concatenatedName ?? ''

    const image = getLargestImage(track.images, 'PRIMARY')

    if (!track.id) {
        logError(target, 'track missing id')
    }

    // Signed out, or offered only its preview, a track plays as its 30-second sample.
    const sample =
        !getPlaybackEnv().auth.isSignedIn() || isPreviewOnlySelections(track.playParams?.selections)
    // Owned/uploaded content (a purchased MP3 with no catalog asin) carries a
    // library `objectid` (COID) identifier and streams from DMLS by COID, not
    // the catalog asin path — vinyl-amzn's coid track factory handles it.
    const isOwnedCoid = mrn?.mediaIdentifierType === MrnIdentifierType.OBJECT_ID
    const musicVideo = !sample && track.mediaType === 'VIDEO'
    const title = track.title ?? ''

    return {
        id: track.id ?? createShortUid(),
        entity: track,
        albumTitle: track.album?.shortTitle ?? track.album?.title ?? '',
        artistName,
        duration: track.duration ?? 0,
        image: image,
        title,
        ...(sample && { metricId: previewMetricId(track.id ?? identifier) }),
        ...(musicVideo && { video: true }),
        trackLoadOptions: musicVideo
            ? () => getPlaybackEnv().resolveMusicVideo(identifier, title)
            : sample
              ? {
                    // The `sample://` scheme routes vinyl-amzn's SampleTrack to
                    // the DMLS sample endpoint, which needs no customer authorization.
                    type: 'sample',
                    uri: `sample://${identifier}`,
                }
              : isOwnedCoid
                ? { type: 'coid', uri: `coid://${identifier}` }
                : { type: 'asin', uri: `asin://${identifier}` },
    }
}
